import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { RoomsFacade } from '@library/store/rooms/rooms.facade';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { LangPipe } from '@library/utils/pipes/lang.pipe';
import { ControlPageCardService } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-sidebar/control-page-card.service';
import { ControlPageStore, GraphType } from '@pages/control-page/control-page-store.service';
import { CO2_MODULES, FAVORITE_TEMPERATURE_MODULES, HUMIDITY_MODULES, NOISE_MODULES, PRESSURE_MODULES, RAIN_MODULES, SUPPORTED_CONTROL_ENERGY_MODULES, SUPPORTED_CONTROL_WEATHER_MODULES, TEMPERATURE_MODULES, WIND_MODULES } from '@pages/control-page/control-page.const';
import { Enums } from 'constants/Enums.constant';
import { Subject, combineLatest, distinctUntilKeyChanged, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-control-page-graph-module-selector',
  templateUrl: './control-page-graph-module-selector.component.html',
  styleUrls: ['./control-page-graph-module-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ControlPageGraphModuleSelectorComponent {

  formControl = new FormControl<{value: string, label: string}>(null);

  private weatherModules$ = new Subject<{value: string, label: string}[]>();
  private energyModules$ = new Subject<{value: string, label: string}[]>();

  viewModel$ = combineLatest({
    weatherModules: this.weatherModules$,
    energyModules: this.energyModules$,
  });

  constructor(
    private controlPageStore: ControlPageStore,
    private modulesFacade: ModulesFacade,
    private homesFacade: HomesFacade,
    private roomsFacade: RoomsFacade,
    private controlPageCardService: ControlPageCardService,
    private lp: LangPipe,
  ) {}

  ngOnInit() {
    /**
     * Whenever the selected card is updated, we need to get the modules that
     * have the same measure type in the selcted room and add them in the dropdown
     */
    this.controlPageStore.card$.pipe(isDefined())
      .subscribe(async ({room, module, graphType}) => {
        const roomId = room?.id ?? module?.room_id;
        room = await firstValueFrom(this.roomsFacade.getRoomById(roomId));
        const roomModules = await firstValueFrom(this.modulesFacade.getCurrentHomeModulesByRoomId(roomId));
        const supportedModules = roomModules.filter(m => ([
          ...SUPPORTED_CONTROL_WEATHER_MODULES,
          ...SUPPORTED_CONTROL_ENERGY_MODULES,
        ].includes(m.type)));

        const moduleTypes = GraphTypeToModuleTypes[graphType];

        const modules = supportedModules.filter(m => moduleTypes.includes(m.type));

        const energyModules = modules.filter(m => SUPPORTED_CONTROL_ENERGY_MODULES.includes(m.type));
        const thermostat = energyModules.find(m => m.type === Enums.ModuleType.NATHERM1);
        const valves = energyModules.filter(m => m.type === Enums.ModuleType.NRV);

        let energyModule = null;
        if (thermostat) {
          energyModule = {value: thermostat.id, label: thermostat.name ?? this.lp.transform('energie-app.__THERM_THERMOSTAT')};
        }
        else if (valves.length > 1) {
          energyModule = {value: valves[0].id, label: this.lp.transform('energie-app.__VALVES')};
        }
        else if (valves.length === 1) {
          energyModule = {value: valves[0].id, label: valves[0].name ?? this.lp.transform('energie-app.__VALVE')};
        }

        this.energyModules$.next(energyModule ? [energyModule] : []);

        const home = await firstValueFrom(this.homesFacade.currentHome$);
        const weatherModules = modules
          .filter(m => {
            if (home.mode !== 'favorite') {
              return SUPPORTED_CONTROL_WEATHER_MODULES.includes(m.type);
            }
            else {
              return FAVORITE_TEMPERATURE_MODULES.includes(m.type);
            }
          })
          .map(m => ({value: m.id, label: m.name ?? this.lp.transform(PRODUCT_NAMES[m.type])}));
        this.weatherModules$.next(weatherModules);

        const selectedModule = module
          ? weatherModules.find(m => m.value === module.id)
          : energyModule;

        if (selectedModule) {
          this.formControl.setValue(selectedModule);
        }
      });

    // Select the correct module/room depending on what is clicked in the dropdown
    this.formControl.valueChanges.pipe(
      distinctUntilKeyChanged('value'),
    ).subscribe(async ({value}) => {
      const moduleId = value;
      const {graphType} = await firstValueFrom(this.controlPageStore.card$);
      const [module] = await firstValueFrom(this.modulesFacade.getModulesById([moduleId]));
      const room = await firstValueFrom(this.roomsFacade.getRoomById(module.room_id));

      switch (graphType) {
        case GraphType.TEMPERATURE:
        case GraphType.THERM:
          switch (module.type) {
            case Enums.ModuleType.NAMAIN:
            case Enums.ModuleType.NAMODULE1:
            case Enums.ModuleType.NAMODULE4:
              this.controlPageCardService.selectTemperature(module);
              break;
            case Enums.ModuleType.NATHERM1:
            case Enums.ModuleType.NRV:
              this.controlPageCardService.selectTherm(room);
              break;
          }
          break;
        case GraphType.HUMIDITY:
          this.controlPageCardService.selectHumidity(module);
          break;
        case GraphType.RAIN:
          this.controlPageCardService.selectRain(module);
          break;
        case GraphType.WIND:
          this.controlPageCardService.selectWind(module);
          break;
        case GraphType.PRESSURE:
          this.controlPageCardService.selectPressure(module);
          break;
        case GraphType.NOISE:
          this.controlPageCardService.selectNoise(module);
          break;
        case GraphType.CO2:
          this.controlPageCardService.selectCO2(module);
          break;
      }

    });
  }
}

const PRODUCT_NAMES = {
  [Enums.ModuleType.NAMAIN]: 'station-settings.__WEATHER_STATION',
  [Enums.ModuleType.NAMODULE1]: 'station-settings.__MY_DEVICE_NAMODULE1',
  [Enums.ModuleType.NAMODULE2]: 'station-settings.__MY_DEVICE_NAMODULE2',
  [Enums.ModuleType.NAMODULE3]: 'station-settings.__MY_DEVICE_NAMODULE3',
  [Enums.ModuleType.NAMODULE4]: 'station-settings.__MY_DEVICE_NAMODULE4',
};

const GraphTypeToModuleTypes = {
  [GraphType.THERM]: TEMPERATURE_MODULES,
  [GraphType.TEMPERATURE]: TEMPERATURE_MODULES,
  [GraphType.HUMIDITY]: HUMIDITY_MODULES,
  [GraphType.RAIN]: RAIN_MODULES,
  [GraphType.WIND]: WIND_MODULES,
  [GraphType.NOISE]: NOISE_MODULES,
  [GraphType.PRESSURE]: PRESSURE_MODULES,
  [GraphType.CO2]: CO2_MODULES,
};